/**
 * Creates a microtask scheduler that batches reactions and executes them in the next microtask.
 * @returns A function that schedules a reaction to be executed in the next microtask
 */
export function microtaskScheduler() {
  const reactions = new Set<Function>();
  let scheduled = false;

  return (reaction: Function) => {
    reactions.add(reaction);
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      reactions.forEach((reaction) => reaction());
      reactions.clear();
    });
  };
}

/**
 * Creates a debounce scheduler that delays execution of reactions until after a specified delay
 * has passed since it was last called.
 * @param delay The time in milliseconds to delay execution (default: 100ms)
 * @returns A function that schedules a reaction to be executed after the delay
 */
export function debounceScheduler(delay = 100) {
  const reactions = new Map<Function, number>();

  return (reaction: Function) => {
    // Clear any existing timeout for this reaction
    const existingTimeout = reactions.get(reaction);
    if (existingTimeout) {
      window.clearTimeout(existingTimeout);
    }

    // Set a new timeout
    const timeoutId = window.setTimeout(() => {
      reaction();
      reactions.delete(reaction);
    }, delay);

    reactions.set(reaction, timeoutId);
  };
}
